// Api
import { getCitActivities } from "@DataPages/api/financeManagement/cit";

// Hooks
import { useSelector } from "react-redux";
import { useBasicPaginatedResource } from "@Common/hooks/Pagination/useBasicPaginatedResource";

// Helpers
import { and } from "@Common/helpers/sonar";

// Types
import { RootState } from "@/redux/store";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { IPageParam } from "@/Common/api/types/pagination";

const PAGE_SIZE = 1500;

export const useCitActivitiesPaginatedQuery = (selected: IPeriodTreeSelection, canRequestCITActivities: boolean) => {
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);

	const { data: activities, ...otherResults } = useBasicPaginatedResource({
		queryKey: ["citActivities", locationId, selected?._id],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const citResponse = await getCitActivities(
				locationId ?? "",
				selected?.cycleId ?? "",
				selected?.payPeriodId ?? "",
				typedPageParam.first,
				typedPageParam.after
			);
			if (!citResponse) {
				throw new Error("Something went wrong while fetching CIT Activities data");
			}

			return citResponse;
		},
		getNextPageParam: (lastPage) => {
			const { pageInfo } = lastPage ?? {};
			return pageInfo?.hasNextPage ? { first: PAGE_SIZE, after: pageInfo.endCursor ?? undefined } : undefined;
		},
		initialPageParam: {
			first: PAGE_SIZE,
			after: "",
		},
		enabled: and(Boolean(locationId), Boolean(selected?.cycleId), Boolean(selected?.payPeriodId), canRequestCITActivities),
		staleTime: Infinity,
		fetchAllPages: true,
	});

	return {
		data: activities,
		...otherResults,
	};
};
